/**
 * Opening Data Assistant (AI Review tab — pre-close only).
 *
 * Backend: eastview-os opening-data-assistant.md.
 *   POST /admin/loans/{dealId}/ai-reviews/opening-assistant/propose   → regenerate
 *   GET  /admin/loans/{dealId}/ai-reviews/opening-assistant?status=…  → the queue
 *   POST /admin/loans/{dealId}/ai-reviews/opening-assistant/{id}/action → act on one
 *
 * The propose-then-accept counterpart to the Loan Opening agent. Same precedence-
 * ordered extract mappers run in a NO-WRITE "queue" mode; each proposal is reviewed
 * per field — nothing hits the loan until Accept. Accept/Edit write via the HITL
 * writeback (provenance-ledgered + revertable — they also appear in Modified Loan
 * Data). Rejecting writes nothing. A proposal onto a manually-set field is refused
 * unless force:true → we re-confirm ("overwrite manual value?").
 *
 * Bank-statement cash-liquidity suggestions ride the same queue (STATED liquidity,
 * never verified); those rows carry a `reasoning` string. Every decision also lands
 * in the loan's Life of Loan Summary + Activity Log (surfaced elsewhere).
 *
 * Admin/underwriter-mounted; pre-close only (AiReviewTab gates on the pipeline).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSnackbar } from "notistack";
import { useDispatch, useSelector } from "react-redux";

import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Collapse from "@mui/material/Collapse";
import CircularProgress from "@mui/material/CircularProgress";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";
import Tooltip from "@mui/material/Tooltip";
import AutoFixHighOutlined from "@mui/icons-material/AutoFixHighOutlined";
import CloseRounded from "@mui/icons-material/CloseRounded";
import DescriptionOutlined from "@mui/icons-material/DescriptionOutlined";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import ChevronRightRounded from "@mui/icons-material/ChevronRightRounded";
import WarningAmberRounded from "@mui/icons-material/WarningAmberRounded";
import CheckCircleRounded from "@mui/icons-material/CheckCircleRounded";
import OpenInFullRounded from "@mui/icons-material/OpenInFullRounded";

import Flx from "../../../components/layout/Flx";
import Txt from "../../../components/typography/Txt";
import { ink, slate } from "../../../theme/colors";
import { losAiReviewsApi } from "../../../api/losAiReviewsApi";
import { normalizeIsoStringToUtc } from "../../../_src_shared/utils/functions/formatDateTime";
import { filePreviewSetData } from "../../../redux/reducers/filePreviewReducer";
import ExtractTrifoldModal from "../tabs/LoanDrilldownDocumentsTab/extracts/ExtractTrifoldModal";
import { useLoanExtractsIndex } from "../tabs/LoanDrilldownDocumentsTab/extracts/useLoanExtractsIndex";
import { addressLabel } from "../../../_src_shared/utils/functions/addressLabel";

const T = {
  ink: "#171A21",
  ink2: "#4B5563",
  muted: "#8A919E",
  border: "#E4E7EC",
  border2: "#D5D9E0",
  surface: "#FFFFFF",
  surface2: "#FAFBFC",
  accent: "#2E4FD8",
  accentSoft: "#EDF0FD",
  accentInk: "#22399B",
  green: "#0B7A55",
  greenSoft: "#E8F6F0",
  amber: "#9A6700",
  amberSoft: "#FBF3E0",
  red: "#B42318",
  redSoft: "#FBEDEB",
  mono: "ui-monospace, SFMono-Regular, Menlo, monospace",
};

const STATUS_TABS = [
  { key: "pending_review", label: "Pending" },
  { key: "applied", label: "Applied" },
  { key: "rejected", label: "Rejected" },
];

// A freshly created loan is still being populated by the intake pipeline, so the
// Assistant can't propose against it for the first 10 minutes after createDateTime.
const PROPOSE_COOLDOWN_MS = 10 * 60 * 1000;

const stripExt = (name) => String(name || "").replace(/\.[^.]+$/, "");
const firstBlob = (b) =>
  String(b || "")
    .split(",")[0]
    .trim() || null;

// "subjectProperties.0.address.apn" → "Property 1 · APN"-ish readable label.
const ACRONYMS = { apn: "APN", id: "ID", llc: "LLC", uw: "UW", ltv: "LTV" };
const ROOTS = {
  subjectProperties: "Property",
  entities: "Entity",
  borrowers: "Borrower",
  guarantors: "Guarantor",
};
const titleize = (seg) =>
  String(seg)
    .replace(/([a-z0-9])([A-Z])/g, "$1 $2")
    .split(/[\s_]+/)
    .map(
      (w) =>
        ACRONYMS[w.toLowerCase()] || w.charAt(0).toUpperCase() + w.slice(1),
    )
    .join(" ");
// Grouping (matches the Modified Loan Data views). "section" buckets by the
// entity key-path root (subjectProperties.0 → "Property 1"); "document" by the
// source document; "list" is flat.
const VIEW_MODES = [
  { key: "section", label: "By section" },
  { key: "document", label: "By document" },
  { key: "list", label: "All changes" },
];
const sectionGroupOf = (path) => {
  const parts = String(path || "").split(".");
  if (ROOTS[parts[0]] && /^\d+$/.test(parts[1])) {
    return {
      key: `${parts[0]}.${parts[1]}`,
      label: `${ROOTS[parts[0]]} ${Number(parts[1]) + 1}`,
      root: parts[0],
      index: Number(parts[1]),
    };
  }
  return { key: "loan", label: "Loan Details", root: "loan", index: null };
};
// The entity's real name, read off the live loan, for the section sub-label.
const entityNameFor = (root, index, loan) => {
  if (index == null || !loan) return null;
  if (root === "subjectProperties") {
    return addressLabel(loan.subjectProperties?.[index]?.address) || null;
  }
  if (root === "entities") return loan.entities?.[index]?.entityName || null;
  if (root === "borrowers" || root === "guarantors") {
    const b = loan[root]?.[index];
    return `${b?.firstName || ""} ${b?.lastName || ""}`.trim() || null;
  }
  return null;
};

// Render any proposed/current value compactly; objects/arrays → JSON.
const fmtVal = (v) => {
  if (v === null || v === undefined || v === "") return null;
  if (typeof v === "object") {
    try {
      return JSON.stringify(v);
    } catch {
      return String(v);
    }
  }
  return String(v);
};

// Unit-suspicious delta: both numeric, same sign, magnitude ratio ≥ 10× (e.g.
// 6.5 proposed against a 0.065 stored decimal). Flags a likely unit mismatch.
const isUnitSuspicious = (proposed, current) => {
  const p = Number(proposed);
  const c = Number(current);
  if (!Number.isFinite(p) || !Number.isFinite(c) || p === 0 || c === 0)
    return false;
  const ratio = Math.abs(p / c);
  return ratio >= 10 || ratio <= 0.1;
};

// --- Value formatting (currency / percent / boolean / object summary) -------
const CURRENCY_HINTS =
  /price|budget|value|liquidity|holdback|amount|cost|arv|reserve|premium|tax|insurance|payoff|balance/i;
const PERCENT_HINTS = /leverage|ltv|ltc|rate|percent|ownership/i;

const isObjVal = (v) => v !== null && typeof v === "object";

// One-line stand-in for an object/array value (address → fullAddress, member
// array → first member name + "+N more").
const summarizeObject = (v) => {
  if (Array.isArray(v)) {
    const first = v[0] || {};
    const name =
      first.fullName ||
      `${first.firstName || ""} ${first.lastName || ""}`.trim();
    return name
      ? `${name}${v.length > 1 ? ` +${v.length - 1} more` : ""}`
      : `${v.length} item${v.length === 1 ? "" : "s"}`;
  }
  if (v && typeof v === "object") {
    return (
      addressLabel(v) ||
      v.entityName ||
      Object.values(v).find((x) => typeof x === "string") ||
      "Object"
    );
  }
  return String(v);
};

// { text, empty?, note?, obj? } for display in the On loan / Proposed cells.
const fmtDisplay = (v, path) => {
  if (v === null || v === undefined || v === "")
    return { text: "empty", empty: true };
  if (typeof v === "boolean") return { text: v ? "Yes" : "No" };
  if (typeof v === "number") {
    if (PERCENT_HINTS.test(path) && v > 0 && v <= 1.5)
      return { text: `${(v * 100).toFixed(2)}%`, note: String(v) };
    if (CURRENCY_HINTS.test(path))
      return { text: `$${v.toLocaleString("en-US")}` };
    return { text: v.toLocaleString("en-US") };
  }
  if (typeof v === "object") return { text: summarizeObject(v), obj: true };
  return { text: String(v) };
};

// Signed delta between two numbers, for the sub-line under Proposed.
const deltaText = (cur, pro, path) => {
  if (typeof cur !== "number" || typeof pro !== "number") return null;
  const d = pro - cur;
  if (d === 0) return null;
  const sign = d > 0 ? "+" : "−";
  const abs = Math.abs(d);
  let txt;
  if (PERCENT_HINTS.test(path) && Math.abs(pro) <= 1.5 && Math.abs(cur) <= 1.5)
    txt = `${sign}${(abs * 100).toFixed(2)} pts`;
  else if (CURRENCY_HINTS.test(path))
    txt = `${sign}$${abs.toLocaleString("en-US")}`;
  else txt = `${sign}${abs.toLocaleString("en-US")}`;
  return { txt, up: d > 0 };
};

// Human field name = the leaf segment (drop entity index + parents).
const fieldLeafName = (path) => {
  const segs = String(path || "")
    .split(".")
    .filter((s) => !/^\d+$/.test(s));
  return titleize(segs[segs.length - 1] || path);
};

// "Property 1 · 205 5th St" / "Borrower 1 · Jorge Duran" / "Loan" — the entity
// this field belongs to, with its real name from the loan appended.
const contextLabel = (path, loan) => {
  const parts = String(path || "").split(".");
  if (ROOTS[parts[0]] && /^\d+$/.test(parts[1])) {
    const base = `${ROOTS[parts[0]]} ${Number(parts[1]) + 1}`;
    const name = entityNameFor(parts[0], Number(parts[1]), loan);
    return name ? `${base} · ${name}` : base;
  }
  if (/^borrowerEntity/.test(parts[0]))
    return loan?.entities?.[0]?.entityName
      ? `Entity · ${loan.entities[0].entityName}`
      : "Entity";
  return "Loan";
};

// The table columns, shared by the header and every row so they line up.
// Source and Decision are fixed and sized to their contents — the source link is
// capped at SOURCE_LINK_MAX_W and truncates (full name in the title), and the
// decision cell is one Accept button plus two icon buttons — so the extra width
// goes to Field / On loan / Proposed, which is where the values are.
const SOURCE_LINK_MAX_W = 165;

// Columns are assembled per view from two flags:
//   • showSection — the entity-context column ("Property 1 · 205 5th St"). Shown
//     in By-document + All-changes; hidden in By-section (the group header is the
//     section already).
//   • showSource  — the source-document column. Hidden in By-document (the doc is
//     the group header); shown otherwise.
// Field / On loan / Proposed flex to fill; Section / Source / Decision are capped.
const COL = {
  field: "minmax(170px, 1.4fr)",
  section: "minmax(140px, 1fr)",
  onLoan: "minmax(100px, 0.8fr)",
  proposed: "minmax(130px, 1fr)",
  source: "180px",
  decision: "136px",
};
const buildCols = ({ showSection, showSource }) =>
  [
    COL.field,
    showSection && COL.section,
    COL.onLoan,
    COL.proposed,
    showSource && COL.source,
    COL.decision,
  ]
    .filter(Boolean)
    .join(" ");
const buildLabels = ({ showSection, showSource }) =>
  [
    "Field",
    showSection && "Section",
    "On loan",
    "Proposed",
    showSource && "Source",
    "Decision",
  ].filter(Boolean);

// The Edit / Keep-current glyphs in the Decision cell — same outlined chip as the
// old "Edit" button, sized to a square so the pair reads as one control group.
const DECISION_ICON_SX = {
  width: 26,
  height: 26,
  borderRadius: "7px",
  bgcolor: T.surface,
  color: T.ink2,
  border: `1px solid ${T.border2}`,
  "&:hover": { bgcolor: T.surface2, color: T.ink },
};

/**
 * The Admin → Pricing table look, applied to this table.
 *
 * The DSCR Pricing sections (Coupon ladder, Adjustments (LLPA)) are the
 * reference: a section TITLE with a mono row-count chip sitting ABOVE a bordered
 * rounded block, whose header is a 34px small-caps rule and whose rows sit on
 * hairlines with a 20px cell inset. The numbers below are copied from
 * `screens/admin/shared/adminGrid.js` (`ADMIN_GRID_SX` / `AdminGrid`) so the two
 * can be held side by side.
 *
 * Those screens get the look from AG Grid; this one can't — a proposal row
 * carries an inline editor, a full-width object diff and its own decision
 * controls, so it stays a CSS grid wearing the same clothes.
 */
const G = {
  headerH: 34,
  rowMinH: 36,
  pad: "20px",
  gap: "0 18px",
  line: "#e5e7eb", // block border + header rule
  lineSoft: "#eef2f6", // row rules
  faint: slate[400], // header labels
  ink,
  ink2: slate[700],
  hover: slate[50],
  radius: "8px",
};

/** The mono count pill beside a section title (pricingUi's `MetaChip`). */
const MetaChip = ({ children }) => (
  <Box
    component="span"
    sx={{
      fontFamily: T.mono,
      fontSize: 10.5,
      fontWeight: 500,
      color: T.muted,
      bgcolor: G.hover,
      border: `1px solid ${G.line}`,
      borderRadius: "5px",
      px: 0.875,
      py: 0.125,
      whiteSpace: "nowrap",
      flexShrink: 0,
    }}
  >
    {children}
  </Box>
);

/**
 * A titled table block — `PricingSubSection` + `AdminGrid`: a chevron/title/meta
 * row on the page background, then the bordered table under it.
 */
const TableSection = ({
  title,
  meta,
  icon,
  endContent,
  showSection = false,
  showSource = true,
  sort,
  onSort,
  children,
}) => {
  const [open, setOpen] = useState(true);

  return (
    <Box>
      <Flx
        ac
        g={1}
        sx={{
          py: 1.25,
          minWidth: 0,
          cursor: "pointer",
          userSelect: "none",
          "&:hover .oda-section-title": { color: G.ink },
        }}
        onClick={() => setOpen((o) => !o)}
      >
        <ChevronRightRounded
          sx={{
            fontSize: 18,
            color: G.faint,
            flexShrink: 0,
            transition: "transform .18s",
            transform: open ? "rotate(90deg)" : "none",
          }}
        />
        {icon}
        <Txt
          className="oda-section-title"
          title={typeof title === "string" ? title : undefined}
          sx={{
            fontSize: 12.5,
            fontWeight: 600,
            color: G.ink,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
            minWidth: 0,
          }}
        >
          {title}
        </Txt>
        {meta ? <MetaChip>{meta}</MetaChip> : null}
        {endContent ? (
          <Box sx={{ ml: "auto" }} onClick={(e) => e.stopPropagation()}>
            {endContent}
          </Box>
        ) : null}
      </Flx>
      {open ? (
        <Box
          sx={{
            border: `1px solid ${G.line}`,
            borderRadius: G.radius,
            overflow: "hidden",
            bgcolor: T.surface,
          }}
        >
          <ProposalColumnHeader
            showSection={showSection}
            showSource={showSource}
            sort={sort}
            onSort={onSort}
          />
          {children}
        </Box>
      ) : null}
    </Box>
  );
};

// --- Column sorting (click a header to sort every group's rows) -------------
// Which sort key each header label maps to. "Decision" has none (not sortable).
const SORT_KEY_BY_LABEL = {
  Field: "field",
  Section: "section",
  "On loan": "onloan",
  Proposed: "proposed",
  Source: "source",
};

// Comparable value for a proposal row under a sort key. Numbers stay numeric;
// objects sort last; everything else compares as a lowercased string.
const sortValueOf = (row, key, loan) => {
  switch (key) {
    case "field":
      return fieldLeafName(row.fieldPath).toLowerCase();
    case "section":
      return (contextLabel(row.fieldPath, loan) || "").toLowerCase();
    case "onloan":
      return isObjVal(row.currentValue) ? "" : (row.currentValue ?? "");
    case "proposed":
      return isObjVal(row.proposedValue) ? "" : (row.proposedValue ?? "");
    case "source":
      return (row.sourceDocName || "").toLowerCase();
    default:
      return "";
  }
};

const compareBy = (a, b) => {
  const ae = a === "" || a == null;
  const be = b === "" || b == null;
  if (ae && be) return 0;
  if (ae) return 1; // empties sort last
  if (be) return -1;
  if (typeof a === "number" && typeof b === "number") return a - b;
  return String(a).localeCompare(String(b), undefined, { numeric: true });
};

// Sort a COPY of `rows` by the active {key, dir}; no-op when key is null.
const sortRowsBy = (rows, sort, loan) => {
  if (!sort?.key) return rows;
  const out = [...rows].sort((r1, r2) =>
    compareBy(
      sortValueOf(r1, sort.key, loan),
      sortValueOf(r2, sort.key, loan),
    ),
  );
  return sort.dir === "desc" ? out.reverse() : out;
};

// Column header — the admin grid's 34px small-caps rule. Sortable labels are
// clickable (an arrow shows the active column + direction).
const ProposalColumnHeader = ({
  showSection = false,
  showSource = true,
  sort,
  onSort,
}) => {
  const labelSx = {
    fontSize: "10.5px",
    fontWeight: 600,
    letterSpacing: "0.07em",
    textTransform: "uppercase",
    textAlign: "left",
  };
  return (
    <Box
      sx={{
        display: "grid",
        gridTemplateColumns: buildCols({ showSection, showSource }),
        gap: G.gap,
        alignItems: "center",
        px: G.pad,
        minHeight: G.headerH,
        bgcolor: "transparent",
        borderBottom: `1px solid ${G.line}`,
      }}
    >
      {buildLabels({ showSection, showSource }).map((h) => {
        const key = SORT_KEY_BY_LABEL[h];
        if (!key || !onSort) {
          return (
            <Txt key={h} sx={{ ...labelSx, color: G.faint }}>
              {h}
            </Txt>
          );
        }
        const active = sort?.key === key;
        return (
          <Box
            key={h}
            component="button"
            type="button"
            onClick={() => onSort(key)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: "2px",
              minWidth: 0,
              border: "none",
              bgcolor: "transparent",
              p: 0,
              cursor: "pointer",
              font: "inherit",
              "&:hover .oda-sort-lbl": { color: G.ink },
            }}
          >
            <Txt
              className="oda-sort-lbl"
              sx={{ ...labelSx, color: active ? G.ink : G.faint }}
            >
              {h}
            </Txt>
            <ExpandMoreRounded
              sx={{
                fontSize: 14,
                flexShrink: 0,
                color: active ? G.ink : "transparent",
                transition: "transform .15s",
                transform:
                  active && sort.dir === "asc" ? "rotate(180deg)" : "none",
              }}
            />
          </Box>
        );
      })}
    </Box>
  );
};

// --- Object value helpers (compare table + fields/JSON editor) --------------
const eq = (a, b) =>
  JSON.stringify(a === undefined ? null : a) ===
  JSON.stringify(b === undefined ? null : b);
const cloneVal = (v) => (v == null ? v : JSON.parse(JSON.stringify(v)));
// Internal / noisy keys hidden from the diff table + fields editor.
const HIDDEN_KEYS = new Set([
  "_id",
  "apnSource",
  "input_address",
  "displayAddress",
]);

// How many properties differ between current and proposed (for the "N properties
// changed" note). Handles both plain objects and arrays of objects.
const changedKeyCount = (cur, pro) => {
  const a = Array.isArray(pro) ? pro : [pro];
  const b = Array.isArray(cur) ? cur : [cur];
  let n = 0;
  a.forEach((item, i) => {
    const c = b[i] || {};
    const keys = new Set([...Object.keys(item || {}), ...Object.keys(c || {})]);
    keys.forEach((k) => {
      if (!HIDDEN_KEYS.has(k) && !eq(item?.[k], c?.[k])) n += 1;
    });
  });
  return n;
};

// A value as shown in a diff-table cell.
const diffCell = (v) => {
  if (v === null || v === undefined || v === "")
    return (
      <Box component="span" sx={{ color: "#cbd5e1" }}>
        —
      </Box>
    );
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "object")
    return (
      <Box
        component="span"
        sx={{ fontFamily: T.mono, fontSize: 10.5, overflowWrap: "anywhere" }}
      >
        {JSON.stringify(v)}
      </Box>
    );
  return String(v);
};

// The rows of the Current vs Proposed comparison for an object/array value.
const buildDiffRows = (current, proposed) => {
  const rows = [];
  const pushObj = (c, p) => {
    const keys = [
      ...new Set([...Object.keys(c || {}), ...Object.keys(p || {})]),
    ].filter((k) => !HIDDEN_KEYS.has(k));
    keys.forEach((k) =>
      rows.push({ k, cur: c?.[k], pro: p?.[k], changed: !eq(c?.[k], p?.[k]) }),
    );
  };
  if (Array.isArray(proposed)) {
    const curArr = Array.isArray(current) ? current : [];
    const len = Math.max(proposed.length, curArr.length);
    for (let i = 0; i < len; i += 1) {
      rows.push({ section: `Item ${i + 1}` });
      pushObj(curArr[i] || {}, proposed[i] || {});
    }
  } else {
    pushObj(current || {}, proposed || {});
  }
  return rows;
};

// Compare-details table (Property · Current · Proposed), changed rows highlighted.
const ObjectDiffTable = ({ current, proposed }) => {
  const rows = buildDiffRows(current, proposed);
  const DIFF_COLS = "minmax(150px, 0.9fr) 1fr 1fr";
  return (
    <Box
      sx={{
        mt: "8px",
        border: `1px solid ${T.border}`,
        borderRadius: "8px",
        overflow: "hidden",
        bgcolor: T.surface2,
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: DIFF_COLS,
          bgcolor: "#fff",
          borderBottom: `1px solid ${T.border}`,
        }}
      >
        {["Property", "Current", "Proposed"].map((h) => (
          <Box
            key={h}
            sx={{
              px: "12px",
              py: "8px",
              fontSize: 9.5,
              fontWeight: 700,
              letterSpacing: "0.05em",
              textTransform: "uppercase",
              color: T.muted,
            }}
          >
            {h}
          </Box>
        ))}
      </Box>
      {rows.map((r, i) =>
        r.section ? (
          <Box
            key={`s${i}`}
            sx={{
              px: "12px",
              py: "6px",
              bgcolor: "#f1f5f9",
              fontSize: 11,
              fontWeight: 600,
              color: T.ink2,
            }}
          >
            {r.section}
          </Box>
        ) : (
          <Box
            key={`${r.k}-${i}`}
            sx={{
              display: "grid",
              gridTemplateColumns: DIFF_COLS,
              borderTop: `1px solid ${T.border}`,
              bgcolor: r.changed ? "#fffdf5" : "transparent",
            }}
          >
            <Box
              sx={{
                px: "12px",
                py: "7px",
                fontFamily: T.mono,
                fontSize: 10.5,
                color: r.changed ? T.amber : T.ink2,
                fontWeight: r.changed ? 600 : 400,
                overflowWrap: "anywhere",
              }}
            >
              {r.k}
            </Box>
            <Box sx={{ px: "12px", py: "7px", fontSize: 11.5, color: T.muted }}>
              {diffCell(r.cur)}
            </Box>
            <Box
              sx={{
                px: "12px",
                py: "7px",
                fontSize: 11.5,
                fontWeight: 600,
                color: T.ink,
              }}
            >
              {diffCell(r.pro)}
            </Box>
          </Box>
        ),
      )}
    </Box>
  );
};

// Which control a key gets, from the draft value (falling back to the current).
const inferKind = (draftVal, curVal) => {
  const pick = draftVal != null ? draftVal : curVal;
  if (pick == null) return "string";
  if (typeof pick === "boolean") return "boolean";
  if (typeof pick === "number") return "number";
  if (Array.isArray(pick))
    return pick.every((x) => typeof x === "string" || typeof x === "number")
      ? "list"
      : "nested";
  if (typeof pick === "object") return "nested";
  return "string";
};
const setAt = (obj, path, val) => {
  const parts = path.split(".");
  let o = obj;
  for (let i = 0; i < parts.length - 1; i += 1) {
    const k = parts[i];
    if (o[k] == null) o[k] = /^\d+$/.test(parts[i + 1]) ? [] : {};
    o = o[k];
  }
  o[parts[parts.length - 1]] = val;
};
const buildFieldList = (draft, current) => {
  const out = [];
  const pushObj = (d, c, prefix) => {
    const keys = [
      ...new Set([...Object.keys(d || {}), ...Object.keys(c || {})]),
    ].filter((k) => !HIDDEN_KEYS.has(k));
    keys.forEach((k) =>
      out.push({
        path: prefix ? `${prefix}.${k}` : k,
        key: k,
        draftVal: d?.[k],
        curVal: c?.[k],
      }),
    );
  };
  if (Array.isArray(draft)) {
    const curArr = Array.isArray(current) ? current : [];
    draft.forEach((item, i) => {
      out.push({ section: `Item ${i + 1}` });
      pushObj(item || {}, curArr[i] || {}, String(i));
    });
  } else {
    pushObj(draft || {}, current || {}, "");
  }
  return out;
};

// One key/value control in the Fields editor.
const KIND_LABEL = {
  boolean: "bool",
  number: "num",
  list: "list",
  nested: "obj",
  string: "text",
};
const FieldControl = ({ path, keyName, draftVal, curVal, onChange }) => {
  const kind = inferKind(draftVal, curVal);
  const changed = !eq(draftVal, curVal);
  const curShown =
    curVal == null
      ? "empty"
      : kind === "boolean"
        ? curVal
          ? "Yes"
          : "No"
        : typeof curVal === "object"
          ? JSON.stringify(curVal)
          : String(curVal);

  let control;
  if (kind === "boolean") {
    control = (
      <Flx
        ac
        sx={{
          border: `1px solid ${T.border}`,
          borderRadius: "7px",
          overflow: "hidden",
          width: "fit-content",
        }}
      >
        {[
          { v: true, l: "Yes" },
          { v: false, l: "No" },
          { v: null, l: "Empty" },
        ].map((o) => {
          const on = draftVal === o.v;
          return (
            <Box
              key={o.l}
              component="button"
              type="button"
              onClick={() => onChange(path, o.v)}
              sx={{
                border: "none",
                cursor: "pointer",
                font: "inherit",
                fontSize: 12,
                fontWeight: 600,
                px: 1.5,
                py: "6px",
                color: on ? "#fff" : T.ink2,
                bgcolor: on ? T.accent : "#fff",
              }}
            >
              {o.l}
            </Box>
          );
        })}
      </Flx>
    );
  } else if (kind === "nested") {
    control = (
      <TextField
        size="small"
        fullWidth
        multiline
        minRows={3}
        defaultValue={JSON.stringify(draftVal ?? null, null, 2)}
        onChange={(e) => {
          try {
            onChange(path, JSON.parse(e.target.value));
          } catch {
            /* keep last valid until parseable */
          }
        }}
        InputProps={{ sx: { fontSize: 11, fontFamily: T.mono } }}
      />
    );
  } else if (kind === "list") {
    control = (
      <TextField
        size="small"
        fullWidth
        defaultValue={(draftVal || []).join(", ")}
        placeholder="comma-separated"
        onChange={(e) =>
          onChange(
            path,
            e.target.value
              .split(",")
              .map((x) => x.trim())
              .filter(Boolean),
          )
        }
        InputProps={{ sx: { fontSize: 12.5 } }}
      />
    );
  } else if (kind === "number") {
    control = (
      <TextField
        size="small"
        fullWidth
        type="number"
        value={draftVal == null ? "" : draftVal}
        placeholder="empty"
        onChange={(e) => {
          const v = e.target.value;
          onChange(path, v === "" ? null : Number(v));
        }}
        InputProps={{ sx: { fontSize: 12.5 } }}
      />
    );
  } else {
    control = (
      <TextField
        size="small"
        fullWidth
        value={draftVal == null ? "" : String(draftVal)}
        placeholder="empty"
        onChange={(e) =>
          onChange(path, e.target.value === "" ? null : e.target.value)
        }
        InputProps={{ sx: { fontSize: 12.5 } }}
      />
    );
  }

  return (
    <Box sx={{ minWidth: 0 }}>
      <Flx ac sx={{ gap: 0.5, mb: "5px" }}>
        <Box
          component="code"
          sx={{
            fontFamily: T.mono,
            fontSize: 11,
            fontWeight: 500,
            color: changed ? T.amber : T.ink2,
          }}
        >
          {keyName}
        </Box>
        <Box
          component="span"
          sx={{
            fontSize: 9.5,
            fontWeight: 600,
            px: "5px",
            borderRadius: "4px",
            bgcolor: "#f1f5f9",
            border: `1px solid ${T.border}`,
            color: T.muted,
          }}
        >
          {KIND_LABEL[kind]}
        </Box>
      </Flx>
      {control}
      <Flx ac sx={{ gap: 0.75, mt: "4px", minWidth: 0 }}>
        <Txt
          sx={{
            fontSize: 10.5,
            color: T.muted,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          Current: {curShown}
        </Txt>
        {changed && curVal !== undefined && (
          <Box
            component="button"
            type="button"
            onClick={() => onChange(path, cloneVal(curVal ?? null))}
            sx={{
              border: "none",
              bgcolor: "transparent",
              cursor: "pointer",
              font: "inherit",
              fontSize: 10.5,
              fontWeight: 600,
              color: T.accent,
              p: 0,
              flexShrink: 0,
              "&:hover": { textDecoration: "underline" },
            }}
          >
            Use current
          </Box>
        )}
      </Flx>
    </Box>
  );
};

// Object editor: Fields ⇄ JSON, Save & accept / Cancel. Mirrors the reference.
const ObjectEditor = ({ proposed, current, busy, onSave, onCancel }) => {
  const [mode, setMode] = useState("fields");
  const [draft, setDraft] = useState(() => cloneVal(proposed));
  const [jsonText, setJsonText] = useState(() =>
    JSON.stringify(proposed, null, 2),
  );
  const [jsonErr, setJsonErr] = useState(false);

  const updateAt = (path, val) =>
    setDraft((d) => {
      const nd = Array.isArray(d) ? cloneVal(d) : { ...d };
      setAt(nd, path, val);
      return nd;
    });

  const switchMode = (target) => {
    if (target === mode) return;
    if (mode === "json") {
      try {
        setDraft(JSON.parse(jsonText));
        setJsonErr(false);
      } catch {
        setJsonErr(true);
        return;
      }
    } else {
      setJsonText(JSON.stringify(draft, null, 2));
    }
    setMode(target);
  };

  const save = () => {
    if (mode === "json") {
      try {
        onSave(JSON.parse(jsonText));
      } catch {
        setJsonErr(true);
      }
    } else {
      onSave(draft);
    }
  };

  const fields = mode === "fields" ? buildFieldList(draft, current) : [];

  return (
    <Box
      sx={{
        mt: "10px",
        bgcolor: T.surface2,
        border: `1px solid ${T.border}`,
        borderRadius: "8px",
        p: "14px",
      }}
    >
      <Flx ac jb wrap sx={{ gap: 1, mb: "12px" }}>
        <Flx
          ac
          sx={{
            border: `1px solid ${T.border}`,
            borderRadius: "7px",
            overflow: "hidden",
            bgcolor: "#fff",
          }}
        >
          {["fields", "json"].map((m) => (
            <Box
              key={m}
              component="button"
              type="button"
              onClick={() => switchMode(m)}
              sx={{
                border: "none",
                cursor: "pointer",
                font: "inherit",
                fontSize: 12,
                fontWeight: 600,
                px: 1.75,
                py: "6px",
                color: mode === m ? "#fff" : T.ink2,
                bgcolor: mode === m ? T.accent : "#fff",
              }}
            >
              {m === "fields" ? "Fields" : "JSON"}
            </Box>
          ))}
        </Flx>
        <Txt sx={{ fontSize: 11, color: T.muted }}>
          {mode === "fields"
            ? "Edit each property. Highlighted properties differ from the current value on the loan."
            : "Edit the raw value. Switching back to Fields needs valid JSON."}
        </Txt>
      </Flx>

      {mode === "fields" ? (
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: "repeat(auto-fill, minmax(230px, 1fr))",
            gap: "14px 16px",
          }}
        >
          {fields.map((f, i) =>
            f.section ? (
              <Box
                key={`sec${i}`}
                sx={{
                  gridColumn: "1 / -1",
                  fontSize: 12,
                  fontWeight: 600,
                  color: T.ink2,
                  borderBottom: `1px solid ${T.border}`,
                  pb: "4px",
                }}
              >
                {f.section}
              </Box>
            ) : (
              <FieldControl
                key={f.path}
                path={f.path}
                keyName={f.key}
                draftVal={f.draftVal}
                curVal={f.curVal}
                onChange={updateAt}
              />
            ),
          )}
        </Box>
      ) : (
        <>
          <TextField
            size="small"
            fullWidth
            multiline
            minRows={8}
            value={jsonText}
            onChange={(e) => {
              setJsonText(e.target.value);
              setJsonErr(false);
            }}
            InputProps={{ sx: { fontSize: 11.5, fontFamily: T.mono } }}
          />
          {jsonErr && (
            <Txt sx={{ fontSize: 11, color: T.red, mt: "6px" }}>
              Invalid JSON — fix it before saving.
            </Txt>
          )}
        </>
      )}

      <Flx ac sx={{ gap: "8px", mt: "12px" }}>
        <Button
          size="small"
          disableElevation
          disabled={busy}
          onClick={save}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 600,
            padding: "7px 15px",
            borderRadius: "7px",
            bgcolor: T.accent,
            color: "#fff",
            "&:hover": { bgcolor: T.accentInk, color: "#fff" },
          }}
        >
          Save &amp; accept
        </Button>
        <Button
          size="small"
          variant="text"
          disabled={busy}
          onClick={onCancel}
          sx={{
            textTransform: "none",
            fontSize: "12px",
            fontWeight: 600,
            padding: "7px 10px",
            borderRadius: "7px",
            color: T.ink2,
            bgcolor: "transparent",
            "&:hover": { color: T.ink, bgcolor: "#fff" },
          }}
        >
          Cancel
        </Button>
      </Flx>
    </Box>
  );
};

// One proposal — a grid row (Field · [Section] · On loan · Proposed · [Source] ·
// Decision). Section/Source columns toggle per view (see buildCols).
const ProposalRow = ({
  row,
  busy,
  loan,
  showSection = false,
  showSource = true,
  onApply,
  onEdit,
  onReject,
  onOpenSource,
}) => {
  const [showReason, setShowReason] = useState(false);
  const [editing, setEditing] = useState(false);
  const [objOpen, setObjOpen] = useState(false); // Compare-details (object diff)
  const [editVal, setEditVal] = useState("");

  const name = useMemo(() => fieldLeafName(row.fieldPath), [row.fieldPath]);
  const ctx = useMemo(
    () => contextLabel(row.fieldPath, loan),
    [row.fieldPath, loan],
  );
  const cur = fmtDisplay(row.currentValue, row.fieldPath);
  const pro = fmtDisplay(row.proposedValue, row.fieldPath);
  const delta = deltaText(row.currentValue, row.proposedValue, row.fieldPath);
  const greenfield = row.currentValue == null;
  const suspicious = isUnitSuspicious(row.proposedValue, row.currentValue);
  const actionable = row.status === "pending_review";
  const isObj = isObjVal(row.proposedValue);

  const startEdit = () => {
    setEditVal(
      isObj ? fmtVal(row.proposedValue) : (fmtVal(row.proposedValue) ?? ""),
    );
    setEditing(true);
  };

  const cellSx = { minWidth: 0, alignSelf: "center" };

  // Hairline rules between rows, none under the last one — the block's own
  // border closes it off (the admin grid's `.ag-row` / `.ag-row-last` pair).
  const tinted = suspicious || !actionable;

  return (
    <Box
      sx={{
        px: G.pad,
        py: "9px",
        borderBottom: `1px solid ${G.lineSoft}`,
        "&:last-of-type": { borderBottom: "none" },
        bgcolor: suspicious
          ? T.amberSoft
          : actionable
            ? "transparent"
            : "#fafbfd",
        ...(tinted ? null : { "&:hover": { bgcolor: G.hover } }),
      }}
    >
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: buildCols({ showSection, showSource }),
          gap: G.gap,
          alignItems: "center",
          minHeight: G.rowMinH - 18,
        }}
      >
        {/* Field: human name + full data-key path underneath — matched to the
            Modified Loan Data table's Field cell. The entity context moves to the
            Section column (shown in By-document / All-changes). */}
        <Box sx={cellSx}>
          <Flx ac sx={{ gap: 0.75, minWidth: 0 }}>
            <Txt
              sx={{
                fontSize: 13,
                fontWeight: 500,
                color: actionable ? "#1e293b" : T.muted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {name}
            </Txt>
            {greenfield && actionable && (
              <Chip
                size="small"
                label="new"
                sx={{
                  flexShrink: 0,
                  height: 16,
                  fontSize: 9,
                  fontWeight: 700,
                  bgcolor: T.greenSoft,
                  color: T.green,
                }}
              />
            )}
            {suspicious && (
              <Tooltip title="Proposed value differs from the current value by 10× or more — likely a unit mismatch. Check before accepting.">
                <WarningAmberRounded
                  sx={{ fontSize: 14, color: T.amber, flexShrink: 0 }}
                />
              </Tooltip>
            )}
          </Flx>
          <Txt
            title={row.fieldPath}
            sx={{
              fontSize: 11,
              fontFamily: "ui-monospace, monospace",
              color: "#94a3b8",
              display: "block",
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {row.fieldPath}
          </Txt>
        </Box>

        {/* Section — the entity this field belongs to ("Property 1 · 205 5th
            St"). Only in By-document / All-changes; By-section groups by it. */}
        {showSection && (
          <Box sx={cellSx}>
            <Txt
              title={ctx}
              sx={{
                fontSize: 12,
                color: actionable ? T.ink2 : T.muted,
                overflow: "hidden",
                textOverflow: "ellipsis",
                whiteSpace: "nowrap",
              }}
            >
              {ctx}
            </Txt>
          </Box>
        )}

        {/* On loan (current) — same padding as the Proposed chip but no bg, so
            the two values line up exactly. */}
        <Box sx={cellSx}>
          <Box
            component="span"
            sx={{
              display: "inline-block",
              px: 1,
              py: "3px",
              maxWidth: "100%",
              fontSize: 12.5,
              color: cur.empty ? T.muted : G.ink2,
              fontStyle: cur.empty ? "italic" : "normal",
              overflowWrap: "anywhere",
            }}
          >
            {cur.text}
          </Box>
        </Box>

        {/* Proposed. Scalars edit inline; objects keep the summary chip and get
            the Fields/JSON editor below the row (see ObjectEditor). */}
        <Box sx={cellSx}>
          {editing && !isObj ? (
            <TextField
              size="small"
              value={editVal}
              onChange={(e) => setEditVal(e.target.value)}
              autoFocus
              fullWidth
              InputProps={{ sx: { fontSize: 12, fontFamily: T.mono } }}
            />
          ) : (
            <Box
              component="span"
              sx={{
                display: "inline-block",
                fontSize: 12.5,
                fontWeight: 600,
                color: T.accentInk,
                bgcolor: T.accentSoft,
                borderRadius: "6px",
                px: 1,
                py: "3px",
                maxWidth: "100%",
                overflowWrap: "anywhere",
              }}
            >
              {pro.text}
              {pro.note && (
                <Box
                  component="span"
                  sx={{ color: T.muted, ml: 0.5, fontWeight: 400 }}
                >
                  ({pro.note})
                </Box>
              )}
            </Box>
          )}
          {delta && (
            <Txt
              sx={{
                fontSize: 11,
                color: delta.up ? T.green : T.red,
                mt: "4px",
              }}
            >
              {delta.txt}
            </Txt>
          )}
          {/* object: "N properties changed" + Compare details toggle */}
          {isObj && (
            <Flx ac wrap sx={{ gap: 1, mt: "4px" }}>
              <Txt sx={{ fontSize: 11, color: T.muted }}>
                {greenfield
                  ? "new"
                  : `${changedKeyCount(row.currentValue, row.proposedValue)} propert${
                      changedKeyCount(row.currentValue, row.proposedValue) === 1
                        ? "y"
                        : "ies"
                    } changed`}
              </Txt>
              <Box
                component="button"
                type="button"
                onClick={() => setObjOpen((v) => !v)}
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "3px",
                  font: "inherit",
                  fontSize: 11,
                  fontWeight: 600,
                  color: T.accent,
                  bgcolor: "transparent",
                  border: "none",
                  p: 0,
                  cursor: "pointer",
                  "&:hover": { textDecoration: "underline" },
                }}
              >
                {objOpen ? "Hide details" : "Compare details"}
                <ExpandMoreRounded
                  sx={{
                    fontSize: 14,
                    transition: "transform 0.15s ease",
                    transform: objOpen ? "rotate(180deg)" : "none",
                  }}
                />
              </Box>
            </Flx>
          )}
          {/* why toggle (source moved to its own column) */}
          {row.reasoning && (
            <Box
              component="button"
              type="button"
              onClick={() => setShowReason((v) => !v)}
              sx={{
                display: "inline-flex",
                alignItems: "center",
                gap: "3px",
                mt: "6px",
                font: "inherit",
                fontSize: 10.5,
                fontWeight: 600,
                color: T.ink2,
                bgcolor: "transparent",
                border: "none",
                p: 0,
                cursor: "pointer",
                "&:hover": { color: T.accent },
              }}
            >
              <ChevronRightRounded
                sx={{
                  fontSize: 13,
                  color: T.muted,
                  transition: "transform 0.15s ease",
                  transform: showReason ? "rotate(90deg)" : "none",
                }}
              />
              Why
            </Box>
          )}
        </Box>

        {/* Source: the document this value came from. Omitted in By-document
            view, where the document is already the section header. */}
        {showSource && (
        <Box sx={cellSx}>
          {row.sourceDocName ? (
            <>
              <Box
                component={onOpenSource ? "button" : "span"}
                type={onOpenSource ? "button" : undefined}
                onClick={onOpenSource ? () => onOpenSource(row) : undefined}
                title={
                  onOpenSource ? `Open ${row.sourceDocName}` : row.sourceDocName
                }
                sx={{
                  display: "inline-flex",
                  alignItems: "center",
                  gap: "4px",
                  minWidth: 0,
                  maxWidth: SOURCE_LINK_MAX_W,
                  font: "inherit",
                  fontSize: 11.5,
                  fontWeight: 600,
                  textAlign: "left",
                  color: onOpenSource ? T.accent : T.ink2,
                  bgcolor: "transparent",
                  border: "none",
                  p: 0,
                  cursor: onOpenSource ? "pointer" : "default",
                  "&:hover": onOpenSource ? { color: T.accentInk } : undefined,
                }}
              >
                <DescriptionOutlined sx={{ fontSize: 13, flexShrink: 0 }} />
                <Box
                  component="span"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.sourceDocName}
                </Box>
              </Box>
              {row.sourceDocType && (
                <Txt
                  sx={{
                    display: "block",
                    fontSize: 10.5,
                    color: T.muted,
                    mt: "2px",
                    maxWidth: SOURCE_LINK_MAX_W,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                  }}
                >
                  {row.sourceDocType}
                </Txt>
              )}
            </>
          ) : (
            <Txt sx={{ fontSize: 12, color: T.muted }}>—</Txt>
          )}
        </Box>
        )}

        {/* Decision: actions (styled like the Suggested Conditions card). */}
        <Flx
          ac
          sx={{ gap: "6px", justifyContent: "flex-start", flexWrap: "wrap" }}
        >
          {actionable ? (
            editing && !isObj ? (
              <>
                <Button
                  size="small"
                  disableElevation
                  disabled={busy}
                  onClick={() => {
                    setEditing(false);
                    onEdit(row, editVal);
                  }}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "4px 9px",
                    borderRadius: "7px",
                    bgcolor: T.accent,
                    color: "#fff",
                    "&:hover": { bgcolor: T.accentInk, color: "#fff" },
                  }}
                >
                  Save
                </Button>
                <Button
                  size="small"
                  variant="text"
                  disabled={busy}
                  onClick={() => setEditing(false)}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "4px 8px",
                    borderRadius: "7px",
                    color: T.ink2,
                    bgcolor: "transparent",
                    "&:hover": { color: T.ink, bgcolor: T.surface2 },
                  }}
                >
                  Cancel
                </Button>
              </>
            ) : (
              <>
                <Button
                  size="small"
                  disableElevation
                  disabled={busy}
                  onClick={() => onApply(row)}
                  sx={{
                    textTransform: "none",
                    fontSize: "11px",
                    fontWeight: 600,
                    padding: "4px 10px",
                    borderRadius: "7px",
                    bgcolor: T.accent,
                    color: "#fff",
                    "&:hover": { bgcolor: T.accentInk, color: "#fff" },
                  }}
                >
                  Accept
                </Button>
                {/* Edit / Keep current are icon buttons — the decision cell is
                    one text button (Accept) plus two glyphs, which is what lets
                    the column sit at 136px. Labels live in the tooltips. */}
                <Tooltip title="Edit value" disableInteractive>
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={startEdit}
                      aria-label="Edit value"
                      sx={DECISION_ICON_SX}
                    >
                      <EditOutlined sx={{ fontSize: 15 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                <Tooltip title="Keep current" disableInteractive>
                  <span>
                    <IconButton
                      size="small"
                      disabled={busy}
                      onClick={() => onReject(row)}
                      aria-label="Keep current"
                      sx={DECISION_ICON_SX}
                    >
                      <CloseRounded sx={{ fontSize: 15 }} />
                    </IconButton>
                  </span>
                </Tooltip>
                {busy && <CircularProgress size={14} thickness={5} />}
              </>
            )
          ) : row.status === "applied" ? (
            <Flx
              ac
              sx={{
                gap: 0.5,
                fontSize: 11.5,
                fontWeight: 600,
                color: T.green,
                bgcolor: T.greenSoft,
                border: `1px solid #bbf7d0`,
                borderRadius: "99px",
                px: 1.25,
                py: "3px",
              }}
            >
              <CheckCircleRounded sx={{ fontSize: 13 }} />
              Applied
            </Flx>
          ) : (
            <Box
              component="span"
              sx={{
                fontSize: 11.5,
                fontWeight: 600,
                color: T.ink2,
                bgcolor: "#f1f5f9",
                border: `1px solid ${T.border}`,
                borderRadius: "99px",
                px: 1.25,
                py: "3px",
              }}
            >
              Kept current
            </Box>
          )}
        </Flx>
      </Box>

      {/* object value: compare-details diff table (read) or the Fields/JSON
          editor (when editing). Both span the full row width. */}
      {isObj && objOpen && !editing && (
        <ObjectDiffTable
          current={row.currentValue}
          proposed={row.proposedValue}
        />
      )}
      {isObj && editing && (
        <ObjectEditor
          proposed={row.proposedValue}
          current={row.currentValue}
          busy={busy}
          onSave={(val) => {
            setEditing(false);
            onEdit(row, val);
          }}
          onCancel={() => setEditing(false)}
        />
      )}

      {/* reasoning (full-width, below the grid) */}
      {row.reasoning && (
        <Collapse in={showReason} unmountOnExit>
          <Box
            sx={{
              mt: "8px",
              fontSize: 11.5,
              color: T.ink2,
              bgcolor: T.surface2,
              border: `1px solid ${T.border}`,
              borderRadius: "8px",
              p: "9px 11px",
              lineHeight: 1.55,
              whiteSpace: "pre-wrap",
            }}
          >
            {row.reasoning}
          </Box>
        </Collapse>
      )}
    </Box>
  );
};

const OpeningDataAssistantPanel = ({ onCount }) => {
  const { enqueueSnackbar } = useSnackbar();
  const dispatch = useDispatch();
  const loan = useSelector((state) => state.loanDrilldown);
  const dealId = loan?._id;
  const loanDocuments = loan?.loanDocuments;

  const { documents: extractDocs } = useLoanExtractsIndex(dealId);
  const [viewer, setViewer] = useState(null);

  const [allRows, setAllRows] = useState([]);
  const [statusTab, setStatusTab] = useState("pending_review");
  const [viewMode, setViewMode] = useState("section"); // section | document | list
  // Per-table column sort — keyed by group id ("__all__" for the flat list) so
  // sorting one section's table doesn't reorder the others.
  const [sortByGroup, setSortByGroup] = useState({});
  const onSort = useCallback((groupKey, colKey) => {
    setSortByGroup((m) => {
      const cur = m[groupKey];
      const next =
        cur?.key === colKey
          ? { key: colKey, dir: cur.dir === "asc" ? "desc" : "asc" }
          : { key: colKey, dir: "asc" };
      return { ...m, [groupKey]: next };
    });
  }, []);
  const [loading, setLoading] = useState(false);
  const [proposing, setProposing] = useState(false);
  const [expanded, setExpanded] = useState(true);
  const busyRef = useRef(new Set());
  const [busyIds, setBusyIds] = useState([]);

  // Cooldown: block "Generate proposals" until 8 minutes past the loan's create
  // time (createDateTime is a naive UTC ISO — normalize before comparing). A
  // light timer flips it back on when the window passes without a page reload.
  const createdMs = useMemo(() => {
    const raw = loan?.createDateTime;
    if (!raw) return 0;
    const t = new Date(normalizeIsoStringToUtc(raw)).getTime();
    return Number.isNaN(t) ? 0 : t;
  }, [loan?.createDateTime]);
  const readyAt = createdMs ? createdMs + PROPOSE_COOLDOWN_MS : 0;
  const [nowMs, setNowMs] = useState(() => Date.now());
  useEffect(() => {
    if (!readyAt || Date.now() >= readyAt) return undefined;
    const id = setInterval(() => {
      setNowMs(Date.now());
      if (Date.now() >= readyAt) clearInterval(id);
    }, 10000);
    return () => clearInterval(id);
  }, [readyAt]);
  const tooEarly = readyAt > 0 && nowMs < readyAt;
  const minutesLeft = tooEarly
    ? Math.max(1, Math.ceil((readyAt - nowMs) / 60000))
    : 0;

  // One fetch of ALL proposals backs both the active-tab list and the per-status
  // counts on the tab strip; the tabs then filter client-side (no refetch on
  // tab switch).
  const fetchRows = useCallback(async () => {
    if (!dealId) return;
    setLoading(true);
    try {
      const data = await losAiReviewsApi.getOpeningAssistantAll(dealId);
      setAllRows(Array.isArray(data) ? data : []);
    } catch {
      setAllRows([]);
    } finally {
      setLoading(false);
    }
  }, [dealId]);

  useEffect(() => {
    fetchRows();
  }, [fetchRows]);

  const rows = useMemo(
    () => allRows.filter((r) => r?.status === statusTab),
    [allRows, statusTab],
  );
  const statusCounts = useMemo(() => {
    const c = { pending_review: 0, applied: 0, rejected: 0 };
    for (const r of allRows) if (c[r?.status] != null) c[r.status] += 1;
    return c;
  }, [allRows]);

  // Report the pending count up to the tab strip.
  useEffect(() => {
    onCount?.(statusCounts.pending_review);
  }, [onCount, statusCounts.pending_review]);

  const propose = useCallback(async () => {
    if (!dealId || proposing) return;
    if (tooEarly) {
      enqueueSnackbar(
        `This loan was just created — the Assistant can generate proposals about ${minutesLeft} minute${
          minutesLeft === 1 ? "" : "s"
        } from now, once intake finishes populating it.`,
        { variant: "info" },
      );
      return;
    }
    setProposing(true);
    try {
      const res = await losAiReviewsApi.proposeOpeningAssistant(dealId);
      const n = res?.proposals ?? res?.queued ?? 0;
      const liq = res?.bankLiquidity?.queued;
      enqueueSnackbar(
        `Generated ${n} proposal${n === 1 ? "" : "s"}${
          liq ? ` (+${liq} bank-liquidity)` : ""
        }.`,
        { variant: "success" },
      );
      setStatusTab("pending_review");
      fetchRows("pending_review");
    } catch (err) {
      enqueueSnackbar(
        err?.response?.data?.detail ||
          err?.message ||
          "Could not generate proposals.",
        { variant: "error" },
      );
    } finally {
      setProposing(false);
    }
  }, [dealId, proposing, tooEarly, minutesLeft, enqueueSnackbar, fetchRows]);

  // Resolve a proposal's source blob to a viewable extract / doc (same join the
  // findings panel uses), then open the trifold viewer or the file preview.
  const extractIndex = useMemo(() => {
    const map = new Map();
    const put = (k, v) => {
      const key = String(k || "").trim();
      if (key && !map.has(key)) map.set(key, v);
    };
    (extractDocs || []).forEach((d) => {
      const p = d?.parent || {};
      put(p.file_storage_name, d);
      put(stripExt(p.file_storage_name), d);
      put(d?.parent_document_id, d);
    });
    return map;
  }, [extractDocs]);

  const loanDocIndex = useMemo(() => {
    const map = new Map();
    const put = (k, v) => {
      const key = String(k || "").trim();
      if (key && !map.has(key)) map.set(key, v);
    };
    (Array.isArray(loanDocuments) ? loanDocuments : []).forEach((d) => {
      put(d?.file_storage_name, d);
      put(stripExt(d?.file_storage_name), d);
    });
    return map;
  }, [loanDocuments]);

  const openSource = useCallback(
    (row) => {
      const blob = firstBlob(row?.sourceBlob);
      if (!blob) return;
      const stem = stripExt(blob);
      const entry = extractIndex.get(blob) || extractIndex.get(stem);
      if (entry?.parent_document_id) {
        setViewer({
          selectedId: entry.parent_document_id,
          focusField: row.fieldPath,
        });
        return;
      }
      const doc = loanDocIndex.get(blob) || loanDocIndex.get(stem);
      if (doc) dispatch(filePreviewSetData(doc));
    },
    [extractIndex, loanDocIndex, dispatch],
  );

  const canOpen = useCallback(
    (row) => {
      const blob = firstBlob(row?.sourceBlob);
      if (!blob) return false;
      const stem = stripExt(blob);
      return !!(
        extractIndex.get(blob) ||
        extractIndex.get(stem) ||
        loanDocIndex.get(blob) ||
        loanDocIndex.get(stem)
      );
    },
    [extractIndex, loanDocIndex],
  );

  const act = useCallback(
    async (row, payload, successMsg) => {
      const id = row?.queueId;
      if (!dealId || !id || busyRef.current.has(id)) return;
      busyRef.current.add(id);
      setBusyIds((b) => [...b, id]);
      try {
        await losAiReviewsApi.actionOpeningAssistant(dealId, id, payload);
        enqueueSnackbar(successMsg, { variant: "success" });
        fetchRows(statusTab);
      } catch (err) {
        const detail = err?.response?.data?.detail || "";
        // A proposal onto a manually-set field is refused unless force:true.
        if (
          (err?.response?.status === 409 || /manual|force/i.test(detail)) &&
          !payload.force &&
          payload.action !== "reject"
        ) {
          const ok = window.confirm(
            `This field already has a value that was set manually. Overwrite it with the ${
              payload.action === "edit" ? "edited" : "proposed"
            } value?`,
          );
          if (ok) {
            busyRef.current.delete(id);
            setBusyIds((b) => b.filter((x) => x !== id));
            return act(row, { ...payload, force: true }, successMsg);
          }
        } else {
          enqueueSnackbar(detail || err?.message || "Action failed.", {
            variant: "error",
          });
        }
      } finally {
        busyRef.current.delete(id);
        setBusyIds((b) => b.filter((x) => x !== id));
      }
    },
    [dealId, enqueueSnackbar, fetchRows, statusTab],
  );

  const applyOne = useCallback(
    (row) => act(row, { action: "apply" }, "Field applied to the loan."),
    [act],
  );
  const editOne = useCallback(
    (row, editedValue) =>
      act(row, { action: "edit", editedValue }, "Edited value applied."),
    [act],
  );
  const rejectOne = useCallback(
    (row) => act(row, { action: "reject" }, "Proposal rejected."),
    [act],
  );

  const acceptAll = useCallback(async () => {
    const pending = allRows.filter((r) => r.status === "pending_review");
    for (const r of pending) {
      // Sequential so each provenance write lands cleanly; skips manual-value
      // conflicts unless the per-row confirm was taken.
      // eslint-disable-next-line no-await-in-loop
      await act(r, { action: "apply" }, "Field applied to the loan.");
    }
  }, [allRows, act]);

  // Group the visible rows for the selected view. "list" is flat (returns null).
  const groupedForView = useMemo(() => {
    if (viewMode === "list") return null;
    const map = new Map();
    for (const r of rows) {
      let key;
      let label;
      let sublabel = null;
      if (viewMode === "document") {
        key = r.sourceDocName || firstBlob(r.sourceBlob) || "Other";
        label = r.sourceDocName || "Unknown document";
        sublabel = r.sourceDocType || null;
      } else {
        const g = sectionGroupOf(r.fieldPath);
        key = g.key;
        label = g.label;
        sublabel = entityNameFor(g.root, g.index, loan);
      }
      if (!map.has(key)) map.set(key, { key, label, sublabel, rows: [] });
      map.get(key).rows.push(r);
    }
    return [...map.values()];
  }, [rows, viewMode, loan]);

  const pendingInList = statusCounts.pending_review;

  return (
    <Box
      sx={{
        bgcolor: "#fff",
        border: "1px solid #e5e8ee",
        borderRadius: "14px",
        overflow: "hidden",
      }}
    >
      {/* Header */}
      <Flx
        ac
        onClick={() => setExpanded((v) => !v)}
        sx={{
          gap: 1.5,
          p: "16px 22px",
          cursor: "pointer",
          userSelect: "none",
          borderBottom: expanded ? "1px solid #eef1f5" : "none",
          "&:hover": { bgcolor: "#fafbfc" },
        }}
      >
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: "9px",
            background: "linear-gradient(135deg,#eef2ff,#e0e7ff)",
            border: "1px solid #dbe2fb",
            color: T.accent,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <AutoFixHighOutlined sx={{ fontSize: 24 }} />
        </Box>
        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Flx ac sx={{ gap: 1 }}>
            <Txt sx={{ fontSize: 15, fontWeight: 700, color: "#0f172a" }}>
              Opening Data Assistant
            </Txt>
            {pendingInList > 0 && (
              <Chip
                size="small"
                label={`${pendingInList} to review`}
                sx={{
                  height: 20,
                  fontSize: 11,
                  fontWeight: 600,
                  bgcolor: T.accentSoft,
                  color: T.accentInk,
                }}
              />
            )}
          </Flx>
          <Txt
            sx={{
              fontSize: 12.5,
              color: "#64748b",
              display: "block",
            }}
          >
            Field-population proposals from the loan's extracts — accept, edit,
            or reject each one. Nothing writes to the loan until you accept.
          </Txt>
        </Box>
        <Tooltip
          title={
            tooEarly
              ? `This loan was just created. The Assistant can generate proposals in about ${minutesLeft} minute${
                  minutesLeft === 1 ? "" : "s"
                }, once intake finishes populating it.`
              : ""
          }
          placement="top"
        >
          <Box
            component="span"
            sx={{ flexShrink: 0 }}
            onClick={(e) => e.stopPropagation()}
          >
            <Button
              variant="outlined"
              size="small"
              disabled={proposing || tooEarly}
              startIcon={
                proposing ? (
                  <CircularProgress size={13} thickness={5} />
                ) : (
                  <AutoFixHighOutlined sx={{ fontSize: 16 }} />
                )
              }
              onClick={(e) => {
                e.stopPropagation();
                propose();
              }}
              sx={{ textTransform: "none", flexShrink: 0 }}
            >
              {proposing
                ? "Generating…"
                : tooEarly
                  ? `Available in ${minutesLeft} min`
                  : "Generate proposals"}
            </Button>
          </Box>
        </Tooltip>
        <ExpandMoreRounded
          sx={{
            fontSize: 22,
            color: "#94a3b8",
            flexShrink: 0,
            transition: "transform 0.15s ease",
            transform: expanded ? "rotate(180deg)" : "none",
          }}
        />
      </Flx>

      <Collapse in={expanded} unmountOnExit>
        <Box sx={{ p: "14px 22px 20px", bgcolor: "#fff" }}>
          {/* status tabs + accept all */}
          <Flx ac jb wrap sx={{ gap: 1, mb: "16px" }}>
            <Flx ac sx={{ gap: "6px" }}>
              {STATUS_TABS.map((t) => {
                const activeTab = statusTab === t.key;
                const count = statusCounts[t.key] ?? 0;
                return (
                  <Box
                    key={t.key}
                    component="button"
                    type="button"
                    onClick={() => setStatusTab(t.key)}
                    sx={{
                      cursor: "pointer",
                      font: "inherit",
                      fontSize: 12,
                      fontWeight: activeTab ? 600 : 500,
                      px: 1.5,
                      py: "6px",
                      borderRadius: "99px",
                      color: activeTab ? "#fff" : T.ink2,
                      bgcolor: activeTab ? T.ink : "#fff",
                      border: `1px solid ${activeTab ? T.ink : T.border2}`,
                    }}
                  >
                    {t.label}
                    <Box
                      component="span"
                      sx={{
                        ml: 0.75,
                        fontWeight: 700,
                        color: activeTab ? "rgba(255,255,255,0.7)" : T.muted,
                      }}
                    >
                      {count}
                    </Box>
                  </Box>
                );
              })}
            </Flx>
            {/* group-by toggle (right side, same row) + accept all */}
            <Flx ac wrap sx={{ gap: 1 }}>
              {rows.length > 0 && (
                <Flx
                  ac
                  sx={{
                    bgcolor: "#eef3f5",
                    border: "1px solid #e3e9ec",
                    borderRadius: "8px",
                    p: "3px",
                    gap: "2px",
                  }}
                >
                  {VIEW_MODES.map((m) => {
                    const active = viewMode === m.key;
                    return (
                      <Box
                        key={m.key}
                        component="button"
                        type="button"
                        onClick={() => setViewMode(m.key)}
                        sx={{
                          border: "none",
                          cursor: "pointer",
                          font: "inherit",
                          px: 1.25,
                          py: 0.5,
                          borderRadius: "6px",
                          fontSize: 12,
                          fontWeight: 600,
                          whiteSpace: "nowrap",
                          color: active ? "#0f172a" : "#64748b",
                          bgcolor: active ? "#fff" : "transparent",
                          boxShadow: active
                            ? "0 1px 2px rgba(15,23,42,0.08)"
                            : "none",
                        }}
                      >
                        {m.label}
                      </Box>
                    );
                  })}
                </Flx>
              )}
              {statusTab === "pending_review" && pendingInList > 0 && (
                <Button
                  size="small"
                  variant="text"
                  onClick={acceptAll}
                  sx={{ textTransform: "none", fontSize: 12 }}
                >
                  Accept all ({pendingInList})
                </Button>
              )}
            </Flx>
          </Flx>

          {loading ? (
            <Flx ac jc sx={{ py: 4 }}>
              <CircularProgress size={20} />
            </Flx>
          ) : rows.length === 0 ? (
            <Flx column ac jc sx={{ py: 5, gap: 0.75 }}>
              <CheckCircleRounded sx={{ fontSize: 26, color: "#059669" }} />
              <Txt sx={{ fontSize: 13.5, fontWeight: 600, color: "#0f172a" }}>
                {statusTab === "pending_review"
                  ? "No proposals to review"
                  : `Nothing ${statusTab === "applied" ? "applied" : "rejected"} yet`}
              </Txt>
              <Txt
                sx={{ fontSize: 12.5, color: "#94a3b8", textAlign: "center" }}
              >
                {statusTab === "pending_review"
                  ? "Generate proposals to pull field values from the loan's extracts."
                  : "Accepted / rejected proposals show up here as an audit trail."}
              </Txt>
            </Flx>
          ) : (
            <>
              {/* All changes — one titled table (Section + Source both shown). */}
              {groupedForView === null ? (
                <TableSection
                  title="All changes"
                  showSection
                  showSource
                  sort={sortByGroup.__all__}
                  onSort={(col) => onSort("__all__", col)}
                  meta={`${rows.length} ${rows.length === 1 ? "field" : "fields"}`}
                >
                  {sortRowsBy(rows, sortByGroup.__all__, loan).map((row) => (
                    <ProposalRow
                      key={row.queueId}
                      row={row}
                      loan={loan}
                      showSection
                      showSource
                      busy={busyIds.includes(row.queueId)}
                      onApply={applyOne}
                      onEdit={editOne}
                      onReject={rejectOne}
                      onOpenSource={canOpen(row) ? openSource : null}
                    />
                  ))}
                </TableSection>
              ) : (
                // By section / By document — a titled table per group, the way
                // the DSCR sheet stacks its sub-tables.
                <Flx column sx={{ gap: "18px" }}>
                  {groupedForView.map((g) => {
                    const isDoc = viewMode === "document";
                    const canOpenDoc = isDoc && canOpen(g.rows[0]);
                    const gSort = sortByGroup[g.key];
                    const gRows = sortRowsBy(g.rows, gSort, loan);
                    return (
                      <TableSection
                        key={g.key}
                        showSection={isDoc}
                        showSource={!isDoc}
                        sort={gSort}
                        onSort={(col) => onSort(g.key, col)}
                        title={
                          g.sublabel && !isDoc
                            ? `${g.label} · ${g.sublabel}`
                            : g.label
                        }
                        meta={`${g.rows.length} ${
                          g.rows.length === 1 ? "field" : "fields"
                        }`}
                        icon={
                          isDoc ? (
                            <DescriptionOutlined
                              sx={{
                                fontSize: 15,
                                color: T.muted,
                                flexShrink: 0,
                              }}
                            />
                          ) : null
                        }
                        endContent={
                          canOpenDoc ? (
                            <Button
                              variant="text"
                              size="small"
                              onClick={() => openSource(g.rows[0])}
                              endIcon={
                                <OpenInFullRounded sx={{ fontSize: 12 }} />
                              }
                              sx={{
                                textTransform: "none",
                                fontSize: 11.5,
                                fontWeight: 600,
                                flexShrink: 0,
                              }}
                            >
                              View document
                            </Button>
                          ) : null
                        }
                      >
                        {gRows.map((row) => (
                          <ProposalRow
                            key={row.queueId}
                            row={row}
                            loan={loan}
                            showSection={isDoc}
                            showSource={!isDoc}
                            busy={busyIds.includes(row.queueId)}
                            onApply={applyOne}
                            onEdit={editOne}
                            onReject={rejectOne}
                            onOpenSource={canOpen(row) ? openSource : null}
                          />
                        ))}
                      </TableSection>
                    );
                  })}
                </Flx>
              )}
            </>
          )}
        </Box>
      </Collapse>

      <ExtractTrifoldModal
        open={!!viewer}
        dealId={dealId}
        documents={extractDocs}
        selectedId={viewer?.selectedId}
        onSelectId={(id) => setViewer({ selectedId: id, focusField: null })}
        onClose={() => setViewer(null)}
        focusField={viewer?.focusField}
      />
    </Box>
  );
};

export default OpeningDataAssistantPanel;
